<template>
  <system-frame title="Kliendid" section="Turvalisus" hint="Kliendiportaali sisselogimised: mis on lukus, mis blokeeritud ja viimased katsed.">
    <!-- The customer portal's login guard (server/service/portalLogin.js):
         what is locked now, what is blocked, and the latest attempts. -->
    <card class="mb-6">
      <div class="d-flex align-center flex-wrap ga-6">
        <div><div class="text-grey-darken-1 text-body-2">Katseid 24 h</div><div class="text-h5">{{ data ? data.day.total : '…' }}</div></div>
        <div><div class="text-grey-darken-1 text-body-2">Valesid</div><div class="text-h5">{{ data ? data.day.failed : '…' }}</div></div>
        <div><div class="text-grey-darken-1 text-body-2">Sisse saanud</div><div class="text-h5">{{ data ? data.day.ok : '…' }}</div></div>
        <div><div class="text-grey-darken-1 text-body-2">Aadresse</div><div class="text-h5">{{ data ? data.day.ips : '…' }}</div></div>
        <v-spacer />
        <v-btn variant="text" :loading="loading" @click="load"><v-icon start>mdi-refresh</v-icon>Värskenda</v-btn>
      </div>
      <p v-if="data" class="text-grey-darken-1 text-body-2 mt-3 mb-0">
        Leping lukustub {{ data.limits.contractMax }} järjestikuse vale telefoni järel: {{ ladder(data.limits.ladder) }}, siis püsivalt, kuni see avatakse;
        aadress {{ data.limits.ipMax }} vale katse järel {{ data.limits.ipWindow }} minuti jooksul<template v-if="!data.limits.ipBlockOn"> (väljas: TRUST_PROXY pole seatud)</template>.
        aadressi blokeering lõpeb ise.
      </p>
    </card>

    <card class="mb-6">
      <div class="text-h6 mb-2">Lukus lepingud</div>
      <p v-if="data && !data.contracts.length" class="text-grey mb-0">Ühtegi lukus lepingut pole.</p>
      <v-table v-else-if="data" density="compact">
        <thead><tr><th>Leping</th><th>Klient</th><th>Valesid</th><th>Kord</th><th>Lukus kuni</th><th>Aadressid</th><th /></tr></thead>
        <tbody>
          <tr v-for="row in data.contracts" :key="row.contract">
            <td>{{ row.contract }}</td>
            <td>
              <router-link v-if="row.client" :to="{ name: 'client/view/info', params: { clientId: row.client.id } }">{{ row.client.name }}</router-link>
              <span v-else class="text-grey">pole sellist lepingut</span>
            </td>
            <td>{{ row.failures }}</td>
            <td>{{ row.level }}.</td>
            <td>
              <v-chip v-if="row.permanent" color="red" size="x-small" variant="flat">püsiv</v-chip>
              <template v-else>{{ until(row.lockedUntil) }}</template>
            </td>
            <td class="text-caption">{{ row.ips.join(', ') }}</td>
            <td class="text-right"><v-btn size="x-small" color="green" @click="unlock({ contract: row.contract })">Ava</v-btn></td>
          </tr>
        </tbody>
      </v-table>
    </card>

    <card class="mb-6">
      <div class="text-h6 mb-2">Blokeeritud aadressid</div>
      <p v-if="data && !data.ips.length" class="text-grey mb-0">Ühtegi blokeeritud aadressi pole.</p>
      <v-table v-else-if="data" density="compact">
        <thead><tr><th>IP</th><th>Valesid</th><th>Lepinguid proovitud</th><th>Blokeeritud kuni</th><th /></tr></thead>
        <tbody>
          <tr v-for="row in data.ips" :key="row.ip">
            <td>{{ row.ip }}</td>
            <td>{{ row.failures }}</td>
            <td>{{ row.contractCount }} <span class="text-caption text-grey">({{ row.contracts.join(', ') }})</span></td>
            <td>{{ time(row.blockedUntil) }}</td>
            <td class="text-right"><v-btn size="x-small" color="green" @click="unlock({ ip: row.ip })">Ava</v-btn></td>
          </tr>
        </tbody>
      </v-table>
    </card>

    <card>
      <div class="text-h6 mb-2">Viimased katsed</div>
      <v-table v-if="data" density="compact">
        <thead><tr><th>Aeg</th><th>Leping</th><th>Tulemus</th><th>Klient</th><th>IP</th></tr></thead>
        <tbody>
          <tr v-for="a in data.attempts" :key="a.id" :class="{ 'text-grey': a.cleared }">
            <td>{{ dateTime(a.at) }}</td>
            <td>{{ a.contract }}</td>
            <td>
              <v-chip :color="a.success ? 'green' : 'red'" size="x-small" variant="flat">{{ a.success ? 'sisse' : 'vale' }}</v-chip>
              <span v-if="a.cleared" class="text-caption ml-1">avatud</span>
            </td>
            <td>{{ a.clientName || '—' }}</td>
            <td class="text-caption">{{ a.ip || '—' }}</td>
          </tr>
        </tbody>
      </v-table>
    </card>
  </system-frame>
</template>

<script>
import SystemFrame from '@/components/SystemFrame.vue'
import api from '@/api'
import dayjs from 'dayjs'
import { mapActions } from 'vuex'

export default {
  components: { SystemFrame },
  data () {
    return { data: null, loading: false }
  },
  methods: {
    ...mapActions(['showNotification']),
    async load () {
      this.loading = true
      try {
        this.data = await api.getSystemSecurity()
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.loading = false
      }
    },
    async unlock (target) {
      try {
        this.data = await api.unlockSystemSecurity(target)
        this.showNotification({ text: 'Avatud', color: 'success' })
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
    },
    time (date) {
      return date ? dayjs(date).format('HH:mm') : '—'
    },
    // A 24-hour lock ends on another day: then the date too.
    until (date) {
      if (!date) return '—'
      return dayjs(date).isSame(dayjs(), 'day') ? dayjs(date).format('HH:mm') : dayjs(date).format('DD.MM HH:mm')
    },
    // [15, 60, 1440] -> "15 min, 1 h, 24 h"
    ladder (minutes) {
      return (minutes || []).map(m => m % 60 ? m + ' min' : (m / 60) + ' h').join(', ')
    },
    dateTime (date) {
      return date ? dayjs(date).format('DD.MM.YYYY HH:mm:ss') : '—'
    }
  },
  mounted () {
    this.load()
  }
}
</script>
